// src/pages/admin/CandidateReportPage.jsx
// Individual candidate interview report - standardized header

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { trackPageView, trackReportViewed } from '../../utils/analytics';

const API_BASE = process.env.REACT_APP_API_URL || 'https://api.invenire.app';

export default function CandidateReportPage() {
  const { interviewId } = useParams();
  const navigate = useNavigate();
  
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedQuestions, setExpandedQuestions] = useState({});

  useEffect(() => {
    document.title = 'Invenire - Candidate Report';
    trackPageView(`/admin/interviews/${interviewId}/report`, 'Candidate Report');
    trackReportViewed('candidate', interviewId);
  }, [interviewId]);

  const loadReport = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`${API_BASE}/scoring/interviews/${interviewId}/report`);
      
      if (!response.ok) {
        throw new Error('Failed to load candidate report');
      }
      
      const data = await response.json();
      setReport(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [interviewId]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const toggleQuestion = (index) => {
    setExpandedQuestions(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const getScoreColor = (score) => {
    if (!score && score !== 0) return '#6b7280';
    if (score >= 7) return '#22c55e';
    if (score >= 5) return '#3b82f6';
    if (score >= 3) return '#eab308';
    return '#ef4444';
  };

  const getRecommendationStyle = (rec) => {
    switch (rec) {
      case 'strong_yes':
        return { bg: 'rgba(34, 197, 94, 0.2)', color: '#22c55e', label: 'Strong Yes' };
      case 'yes':
        return { bg: 'rgba(59, 130, 246, 0.2)', color: '#3b82f6', label: 'Yes' };
      case 'maybe':
        return { bg: 'rgba(234, 179, 8, 0.2)', color: '#eab308', label: 'Maybe' };
      case 'no':
        return { bg: 'rgba(239, 68, 68, 0.2)', color: '#ef4444', label: 'No' };
      default:
        return { bg: 'rgba(107, 114, 128, 0.2)', color: '#6b7280', label: 'Pending' };
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>
          <div style={styles.spinner}></div>
          <p>Loading candidate report...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.errorContainer}>
          <h2>Failed to Load Report</h2>
          <p style={{ color: '#ef4444' }}>{error}</p>
          <button onClick={loadReport} style={styles.retryButton}>Retry</button>
          <button onClick={() => navigate(-1)} style={styles.backLink}>
            ← Go Back
          </button>
        </div>
      </div>
    );
  }

  const recStyle = getRecommendationStyle(report?.recommendation);

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.logoContainer}>
          <img 
            src="/invenire-logo-transparent.png" 
            alt="Invenire" 
            style={styles.logoImage}
          />
          <span style={styles.subtitle}>Candidate Report</span>
        </div>
        <div style={styles.headerActions}>
          {report?.job_id && (
            <button 
              onClick={() => navigate(`/admin/jobs/${report.job_id}/report`)}
              style={styles.secondaryButton}
            >
              ← Job Report
            </button>
          )}
          <button onClick={() => navigate('/admin/reports')} style={styles.backButton}>
            All Reports
          </button>
        </div>
      </div>

      <div style={styles.content}>
        {/* Candidate Header */}
        <div style={styles.candidateHeader}>
          <div style={styles.candidateInfo}>
            <h1 style={styles.candidateName}>
              {report?.candidate_name || 'Unknown Candidate'}
            </h1>
            {report?.candidate_email && (
              <p style={styles.candidateEmail}>{report.candidate_email}</p>
            )}
            <p style={styles.jobTitle}>{report?.job_title || 'Unknown Position'}</p>
          </div>
          
          <div style={styles.scoreSection}>
            <div style={{
              ...styles.overallScore,
              borderColor: getScoreColor(report?.overall_score),
              color: getScoreColor(report?.overall_score)
            }}>
              {report?.overall_score?.toFixed(1) || '—'}
            </div>
            <span style={{
              ...styles.recBadge,
              backgroundColor: recStyle.bg,
              color: recStyle.color
            }}>
              {recStyle.label}
            </span>
          </div>
        </div>

        {/* Summary */}
        {report?.summary && (
          <div style={styles.summaryCard}>
            <h3 style={styles.sectionTitle}>Summary</h3>
            <p style={styles.summaryText}>{report.summary}</p>
          </div>
        )}

        {/* Strengths & Concerns */}
        <div style={styles.twoColumn}>
          {report?.strengths?.length > 0 && (
            <div style={styles.strengthsCard}>
              <h3 style={styles.sectionTitle}>💪 Strengths</h3>
              <ul style={styles.list}>
                {report.strengths.map((s, i) => (
                  <li key={i} style={styles.listItem}>{s}</li>
                ))}
              </ul>
            </div>
          )}
          
          {report?.concerns?.length > 0 && (
            <div style={styles.concernsCard}>
              <h3 style={styles.sectionTitle}>⚠️ Concerns</h3>
              <ul style={styles.list}>
                {report.concerns.map((c, i) => (
                  <li key={i} style={styles.listItem}>{c}</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Q&A Breakdown */}
        {report?.questions?.length > 0 && (
          <div style={styles.questionsSection}>
            <h3 style={styles.sectionTitle}>Question & Answer Analysis</h3>
            <div style={styles.questionsList}>
              {report.questions.map((q, index) => (
                <div key={index} style={styles.questionCard}>
                  <div 
                    style={styles.questionHeader}
                    onClick={() => toggleQuestion(index)}
                  >
                    <div style={styles.questionInfo}>
                      <span style={styles.questionNumber}>Q{index + 1}</span>
                      <span style={styles.questionText}>{q.question}</span>
                    </div>
                    <div style={styles.questionMeta}>
                      {q.score && (
                        <span style={{
                          ...styles.questionScore,
                          color: getScoreColor(q.score)
                        }}>
                          {q.score.toFixed(1)}
                        </span>
                      )}
                      <span style={styles.expandIcon}>
                        {expandedQuestions[index] ? '▼' : '▶'}
                      </span>
                    </div>
                  </div>
                  
                  {expandedQuestions[index] && (
                    <div style={styles.questionDetails}>
                      <div style={styles.answerSection}>
                        <h4 style={styles.subTitle}>Candidate's Answer</h4>
                        <p style={styles.answerText}>
                          {q.answer || 'No answer recorded'}
                        </p>
                      </div>
                      
                      {q.evaluation && (
                        <div style={styles.evaluationSection}>
                          <h4 style={styles.subTitle}>AI Evaluation</h4>
                          <p style={styles.evaluationText}>{q.evaluation}</p>
                        </div>
                      )}
                      
                      {q.timestamp && (
                        <div style={styles.timestampSection}>
                          <span style={styles.timestamp}>
                            📹 {Math.floor(q.timestamp / 60)}:{String(q.timestamp % 60).padStart(2, '0')}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Video Link */}
        {report?.video_url && (
          <div style={styles.videoSection}>
            <h3 style={styles.sectionTitle}>Interview Recording</h3>
            <a 
              href={report.video_url} 
              target="_blank" 
              rel="noopener noreferrer"
              style={styles.videoLink}
            >
              🎬 Watch Full Interview →
            </a>
          </div>
        )}

        {/* Meta Info */}
        <div style={styles.metaSection}>
          <div style={styles.metaItem}>
            <span style={styles.metaLabel}>Interview ID:</span>
            <span>{interviewId}</span>
          </div>
          {report?.completed_at && (
            <div style={styles.metaItem}>
              <span style={styles.metaLabel}>Completed:</span>
              <span>{new Date(report.completed_at).toLocaleString()}</span>
            </div>
          )}
          {report?.duration && (
            <div style={styles.metaItem}>
              <span style={styles.metaLabel}>Duration:</span>
              <span>{Math.round(report.duration / 60)} minutes</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// STYLES
// =============================================================================

const styles = {
  container: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #11181C 0%, #1a2632 50%, #0a0f14 100%)',
    color: '#fff',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
  },
  loading: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    color: '#9ca3af'
  },
  spinner: {
    width: 40,
    height: 40,
    border: '3px solid rgba(255,255,255,0.1)',
    borderTopColor: '#a855f7',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite'
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    padding: 40
  },
  retryButton: {
    marginTop: 16,
    padding: '10px 20px',
    background: '#a855f7',
    border: 'none',
    borderRadius: 6,
    color: '#fff',
    cursor: 'pointer'
  },
  backLink: {
    marginTop: 12,
    background: 'none',
    border: 'none',
    color: '#60a5fa',
    cursor: 'pointer',
    fontSize: 14
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 40px',
    borderBottom: '1px solid rgba(255,255,255,0.1)'
  },
  logoContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4
  },
  logoImage: {
    height: 48,
    width: 'auto',
    objectFit: 'contain'
  },
  subtitle: {
    fontSize: 14,
    color: '#9ca3af'
  },
  headerActions: {
    display: 'flex',
    gap: 12
  },
  secondaryButton: {
    padding: '8px 16px',
    background: 'transparent',
    border: '1px solid rgba(255,255,255,0.2)',
    color: '#fff',
    borderRadius: 6,
    fontSize: 14,
    cursor: 'pointer'
  },
  backButton: {
    padding: '8px 16px',
    background: 'rgba(255,255,255,0.1)',
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    fontSize: 14,
    cursor: 'pointer'
  },
  content: {
    maxWidth: 900,
    margin: '0 auto',
    padding: '32px 24px'
  },
  candidateHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 32
  },
  candidateInfo: {},
  candidateName: {
    fontSize: 28,
    fontWeight: 700,
    margin: '0 0 8px 0'
  },
  candidateEmail: {
    fontSize: 14,
    color: '#6b7280',
    margin: '0 0 8px 0'
  },
  jobTitle: {
    fontSize: 16,
    color: '#9ca3af',
    margin: 0
  },
  scoreSection: {
    textAlign: 'center'
  },
  overallScore: {
    width: 80,
    height: 80,
    borderRadius: '50%',
    border: '3px solid',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 28,
    fontWeight: 700,
    marginBottom: 12
  },
  recBadge: {
    padding: '6px 16px',
    borderRadius: 16,
    fontSize: 14,
    fontWeight: 600
  },
  summaryCard: {
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 12,
    padding: 24,
    marginBottom: 24
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: '#9ca3af',
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginTop: 0,
    marginBottom: 16
  },
  summaryText: {
    fontSize: 15,
    lineHeight: 1.6,
    color: '#e5e7eb',
    margin: 0
  },
  twoColumn: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 20,
    marginBottom: 24
  },
  strengthsCard: {
    background: 'rgba(34, 197, 94, 0.05)',
    border: '1px solid rgba(34, 197, 94, 0.2)',
    borderRadius: 12,
    padding: 24
  },
  concernsCard: {
    background: 'rgba(239, 68, 68, 0.05)',
    border: '1px solid rgba(239, 68, 68, 0.2)',
    borderRadius: 12,
    padding: 24
  },
  list: {
    margin: 0,
    paddingLeft: 20
  },
  listItem: {
    marginBottom: 8,
    lineHeight: 1.5,
    color: '#e5e7eb'
  },
  questionsSection: {
    marginBottom: 32
  },
  questionsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12
  },
  questionCard: {
    background: 'rgba(255,255,255,0.02)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 8,
    overflow: 'hidden'
  },
  questionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 20px',
    cursor: 'pointer'
  },
  questionInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    flex: 1
  },
  questionNumber: {
    background: 'rgba(168, 85, 247, 0.2)',
    color: '#a855f7',
    padding: '4px 10px',
    borderRadius: 4,
    fontSize: 12,
    fontWeight: 600
  },
  questionText: {
    fontSize: 14,
    color: '#e5e7eb'
  },
  questionMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: 12
  },
  questionScore: {
    fontSize: 16,
    fontWeight: 700
  },
  expandIcon: {
    color: '#6b7280',
    fontSize: 12
  },
  questionDetails: {
    padding: '0 20px 20px',
    borderTop: '1px solid rgba(255,255,255,0.05)'
  },
  answerSection: {
    marginTop: 16
  },
  subTitle: {
    fontSize: 12,
    fontWeight: 600,
    color: '#6b7280',
    textTransform: 'uppercase',
    marginBottom: 8
  },
  answerText: {
    fontSize: 14,
    lineHeight: 1.6,
    color: '#9ca3af',
    margin: 0,
    fontStyle: 'italic'
  },
  evaluationSection: {
    marginTop: 16,
    padding: 16,
    background: 'rgba(168, 85, 247, 0.05)',
    borderRadius: 8
  },
  evaluationText: {
    fontSize: 14,
    lineHeight: 1.6,
    color: '#e5e7eb',
    margin: 0
  },
  timestampSection: {
    marginTop: 12
  },
  timestamp: {
    fontSize: 13,
    color: '#60a5fa'
  },
  videoSection: {
    marginBottom: 32
  },
  videoLink: {
    display: 'inline-block',
    padding: '12px 24px',
    background: 'rgba(59, 130, 246, 0.2)',
    border: '1px solid #3b82f6',
    borderRadius: 8,
    color: '#3b82f6',
    textDecoration: 'none',
    fontWeight: 500
  },
  metaSection: {
    display: 'flex',
    gap: 32,
    padding: 20,
    background: 'rgba(255,255,255,0.02)',
    borderRadius: 8,
    fontSize: 14
  },
  metaItem: {
    display: 'flex',
    gap: 8
  },
  metaLabel: {
    color: '#6b7280'
  }
};
